import React, { useState, useEffect } from 'react';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { StatCard } from '../../components/admin/StatCard';
import { StatCardSkeleton } from '../../components/admin/StatCardSkeleton';
import { DashboardChart } from '../../components/admin/DashboardChart';

interface DashboardStats {
    totalUsers: number;
    totalPlugins: number;
    totalRevenue: number;
    revenueToday: number;
}

// Ícones
const UsersIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M15 21a6 6 0 00-9-5.197M15 12a4 4 0 110 5.292" /></svg>;
const PluginsIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>;
const RevenueIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v.01M12 12v.01M12 12V11m0 1v.01M12 6.01V6" /></svg>;

export default function AdminDashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState<Partial<DashboardStats>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        async function fetchStats() {
            setLoading(true);
            try {
                const response = await apiClient.get('/admin/dashboard/totals');
                setStats(response.data);
            } catch (err: any) {
                setError(err.response?.data?.error || 'Falha ao carregar estatísticas.');
            } finally {
                setLoading(false);
            }
        }
        fetchStats();
    }, []);

    const formatCurrency = (value: number) => {
        return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div className="space-y-8">
            {/* Header com gradiente */}
            <div className="relative bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 rounded-2xl p-8 shadow-lg overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full"></div>
                    <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white rounded-full"></div>
                </div>
                <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-2">
                        <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <h1 className="text-4xl font-extrabold text-white">Dashboard Administrativo</h1>
                    </div>
                    <p className="text-blue-100 text-lg">Bem-vindo(a) de volta, <span className="font-semibold text-white">{user?.name || 'Admin'}</span>! Aqui está um resumo do seu negócio.</p>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-md">
                    <div className="flex items-center gap-3">
                        <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-red-700 font-medium">{error}</p>
                    </div>
                </div>
            )}

            {/* Cards de Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {loading ? (
                    Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
                ) : (
                    <>
                        <StatCard 
                            title="Total de Vendas" 
                            value={formatCurrency(stats.totalRevenue ?? 0)} 
                            icon={<RevenueIcon />}
                            trend="+12.5%"
                            trendLabel="vs. mês anterior"
                        />
                        <StatCard 
                            title="Vendas Hoje" 
                            value={formatCurrency(stats.revenueToday ?? 0)} 
                            icon={<RevenueIcon />}
                            trend="+5.2%"
                            trendLabel="vs. ontem"
                        />
                        <StatCard 
                            title="Clientes Cadastrados" 
                            value={stats.totalUsers ?? 0} 
                            icon={<UsersIcon />}
                            trend="+8.1%"
                            trendLabel="novos este mês"
                        />
                        <StatCard 
                            title="Plugins na Loja" 
                            value={stats.totalPlugins ?? 0} 
                            icon={<PluginsIcon />}
                        />
                    </>
                )}
            </div>

            {/* Gráficos */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <DashboardChart title="Vendas Recentes" metric="daily-sales" dataKey="Vendas" color="#0D63F3" />
                <DashboardChart title="Novos Clientes" metric="daily-registrations" dataKey="Novos Clientes" color="#10B981" />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Ações Rápidas
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <a href="/admin/plugins/new" className="group flex items-center gap-3 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 p-4 rounded-xl transition-all border border-blue-200 hover:border-blue-300 hover:shadow-md">
                        <div className="bg-brand-blue p-2 rounded-lg group-hover:scale-110 transition-transform">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">Adicionar Plugin</p>
                            <p className="text-sm text-gray-600">Novo produto</p>
                        </div>
                    </a>
                    <a href="/admin/users" className="group flex items-center gap-3 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 p-4 rounded-xl transition-all border border-green-200 hover:border-green-300 hover:shadow-md">
                        <div className="bg-green-600 p-2 rounded-lg group-hover:scale-110 transition-transform">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">Gerenciar Usuários</p>
                            <p className="text-sm text-gray-600">Clientes ativos</p>
                        </div>
                    </a>
                    <a href="/admin/licenses" className="group flex items-center gap-3 bg-gradient-to-br from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 p-4 rounded-xl transition-all border border-purple-200 hover:border-purple-300 hover:shadow-md">
                        <div className="bg-purple-600 p-2 rounded-lg group-hover:scale-110 transition-transform">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                        </div>
                        <div>
                            <p className="font-semibold text-gray-900">Licenças</p>
                            <p className="text-sm text-gray-600">Gerenciar acessos</p>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    );
}